import {
  faCircleArrowUp,
  faCirclePause,
  faCircleArrowDown,
} from '@fortawesome/pro-solid-svg-icons'
import { getFetchError } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'

import { Card, CardColors, Icon, Text } from '@/ui'
import {
  INSIGHTS_CHANGE_TYPE,
  INSIGHTS_TYPE,
  formatPercentage,
  insightsCopyConstants,
} from './utils'
import { TextColors, TypographyStyles } from '@/styles'

export const getSummaryIcon = (type: INSIGHTS_CHANGE_TYPE) => {
  switch (type) {
    case INSIGHTS_CHANGE_TYPE.INCREASE:
      return faCircleArrowUp
    case INSIGHTS_CHANGE_TYPE.SAME:
      return faCirclePause
    default:
      return faCircleArrowDown
  }
}

export const SummaryTextForExpenses = ({
  formattedPercentChange,
  formattedPercentCounterpart,
  changeType,
  amount,
  currentMonth,
  prevMonth,
  prevMonthTotal,
  textForDashboardCard,
}: {
  formattedPercentChange: string
  formattedPercentCounterpart: string
  changeType?: INSIGHTS_CHANGE_TYPE
  amount?: string
  currentMonth?: string
  prevMonth?: string
  prevMonthTotal?: string
  textForDashboardCard?: boolean
}) => {
  switch (changeType) {
    case INSIGHTS_CHANGE_TYPE.INCREASE:
      return (
        <Text color={TextColors.MUTED} variant={TypographyStyles.BODY_SM}>
          Your practice&apos;s expense spending of {amount} in {currentMonth} is{' '}
          <b>{formattedPercentChange} higher</b> than {prevMonth}.{' '}
          {formattedPercentCounterpart !== '0%' &&
            ` Your expenses were ${formattedPercentCounterpart} of your income.`}
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.DECREASE:
      return (
        <Text color={TextColors.MUTED} variant={TypographyStyles.BODY_SM}>
          {textForDashboardCard && (
            <>
              <b>Great news!</b>
              <br />
            </>
          )}
          Your practice&apos;s expense spending of {amount} in {currentMonth} is{' '}
          <b>{formattedPercentChange} lower</b> than {prevMonth}.{' '}
          {formattedPercentCounterpart !== '0%' &&
            ` Your expenses were ${formattedPercentCounterpart} of your income.`}
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.SAME:
      return (
        <Text color={TextColors.MUTED} variant={TypographyStyles.BODY_SM}>
          Your practice&apos;s expense spending of {amount} in {currentMonth} is
          about <b>the same as</b> your expense spending in {prevMonth}. Keep
          going!
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.LAST_MONTH_EMPTY:
      return (
        <Text color={TextColors.MUTED} variant={TypographyStyles.BODY_SM}>
          Your practice&apos;s expense spending was {amount} in {currentMonth}{' '}
          compared to $0 in {prevMonth}.
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.EMPTY:
      return (
        <Text color={TextColors.MUTED} variant={TypographyStyles.BODY_SM}>
          Your practice&apos;s expense spending of {amount} was $0 in{' '}
          {currentMonth} compared to {prevMonthTotal} in {prevMonth}.
        </Text>
      )
    case undefined:
      return (
        <Text color={TextColors.MUTED} variant={TypographyStyles.BODY_SM}>
          How much your monthly expense spending increases or decreases will
          show up here.
        </Text>
      )
    default:
      return changeType satisfies never
  }
}

const SummaryTextForIncome = ({
  formattedPercentChange,
  changeType,
  amount,
  currentMonth,
  prevMonth,
  prevMonthTotal,
}: {
  formattedPercentChange: string
  changeType?: INSIGHTS_CHANGE_TYPE
  amount?: string
  currentMonth?: string
  prevMonth?: string
  prevMonthTotal?: string
}) => {
  switch (changeType) {
    case INSIGHTS_CHANGE_TYPE.INCREASE:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s income of {amount} in {currentMonth} is{' '}
          <b>{formattedPercentChange} higher</b> than {prevMonth}. Great job
          increasing your income!
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.DECREASE:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s income of {amount} in {currentMonth} is{' '}
          <b>{formattedPercentChange} lower</b> than {prevMonth}. Look at your
          income transactions to understand why your income might be less.
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.SAME:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s income of {amount} in {currentMonth} is about{' '}
          <b>the same as</b> your expense spending in {prevMonth}. Keep going!
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.LAST_MONTH_EMPTY:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s income was {amount} in {currentMonth} compared to
          $0 in {prevMonth}.
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.EMPTY:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s income was $0 in {currentMonth} compared to{' '}
          {prevMonthTotal} in
          {prevMonth}.
        </Text>
      )
    case undefined:
      return (
        <Text color={TextColors.MUTED}>
          How much your monthly income increases or decreases will show up here.
        </Text>
      )
    default:
      return changeType satisfies never
  }
}

const SummaryTextForProfit = ({
  formattedPercentChange,
  changeType,
  amount,
  currentMonth,
  prevMonth,
}: {
  formattedPercentChange: string
  changeType?: INSIGHTS_CHANGE_TYPE
  amount?: string
  currentMonth?: string
  prevMonth?: string
}) => {
  switch (changeType) {
    case INSIGHTS_CHANGE_TYPE.INCREASE:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s profit of {amount} in {currentMonth} is{' '}
          <b>{formattedPercentChange} higher</b> than {prevMonth}. Great job!
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.DECREASE:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s profit of {amount} in {currentMonth} is{' '}
          <b>{formattedPercentChange} lower</b> than {prevMonth}. Look at your
          income and expenses from last month to understand why your profit
          might be less.
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.SAME:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s profit of {amount} in {currentMonth} is about{' '}
          <b>the same as</b> your profit in {prevMonth}.
        </Text>
      )
    case INSIGHTS_CHANGE_TYPE.LAST_MONTH_EMPTY:
      return (
        <Text color={TextColors.MUTED}>
          Your practice&apos;s profit was {amount} in {currentMonth}.
        </Text>
      )

    case INSIGHTS_CHANGE_TYPE.EMPTY:
    case undefined:
      return (
        <Text color={TextColors.MUTED}>
          How much your monthly profit increases or decreases will show up here.
        </Text>
      )
    default:
      return changeType satisfies never
  }
}

const getSummaryTextExpenses = (
  insightsType: INSIGHTS_TYPE,
  changeType?: INSIGHTS_CHANGE_TYPE,
  amount?: string,
  percentChange?: number,
  currentMonth?: string,
  prevMonth?: string,
  percentCounterpart?: number,
  prevMonthTotal?: string
) => {
  const formattedPercentChange = formatPercentage(percentChange)
  const formattedPercentCounterpart = formatPercentage(percentCounterpart)

  switch (insightsType) {
    case INSIGHTS_TYPE.EXPENSES:
      return (
        <SummaryTextForExpenses
          formattedPercentChange={formattedPercentChange}
          formattedPercentCounterpart={formattedPercentCounterpart}
          changeType={changeType}
          amount={amount}
          currentMonth={currentMonth}
          prevMonth={prevMonth}
          prevMonthTotal={prevMonthTotal}
        />
      )
    case INSIGHTS_TYPE.INCOME:
      return (
        <SummaryTextForIncome
          formattedPercentChange={formattedPercentChange}
          changeType={changeType}
          amount={amount}
          currentMonth={currentMonth}
          prevMonth={prevMonth}
          prevMonthTotal={prevMonthTotal}
        />
      )
    case INSIGHTS_TYPE.PROFIT:
      return (
        <SummaryTextForProfit
          formattedPercentChange={formattedPercentChange}
          changeType={changeType}
          amount={amount}
          currentMonth={currentMonth}
          prevMonth={prevMonth}
        />
      )
    case undefined:
      return (
        <Text color={TextColors.MUTED}>
          {insightsCopyConstants[insightsType].emptyCurrentSummary}
        </Text>
      )
    default:
      return insightsType satisfies never
  }
}

export const CurrentSummaryCard = ({
  headerText,
  type,
  amount,
  percentChange,
  currentMonth,
  prevMonth,
  percentCounterpart,
  insightsType,
  isResponseEmpty,
  prevMonthTotal,
  insightsFetchKey,
}: {
  headerText?: string
  type: INSIGHTS_CHANGE_TYPE
  amount?: string
  percentChange?: number
  currentMonth?: string
  prevMonth?: string
  percentCounterpart?: number
  insightsType: INSIGHTS_TYPE
  isResponseEmpty: boolean
  prevMonthTotal?: string
  insightsFetchKey: string
}) => {
  const insightsSummaryError = useReselector(getFetchError, insightsFetchKey)

  if (insightsSummaryError) {
    return (
      <Card color={CardColors.LEVEL_0} className="p-5 min-h-[150px]">
        <Text variant={TypographyStyles.H3} className="mb-4">
          {headerText}
        </Text>
        <Text color={TextColors.MUTED}>
          Something went wrong - please try again in a bit.
        </Text>
      </Card>
    )
  }

  if (isResponseEmpty) {
    return (
      <Card color={CardColors.LEVEL_0} className="p-5 min-h-[150px]">
        <Text variant={TypographyStyles.H3} className="mb-4">
          <Icon
            icon={getSummaryIcon(type)}
            style={{ marginRight: 8 }}
            rotation={type === 'same' ? 90 : undefined}
          />
          {headerText}
        </Text>
        <Text color={TextColors.MUTED}>
          {insightsCopyConstants[insightsType].emptyCurrentSummary}
        </Text>
      </Card>
    )
  }

  return (
    <Card
      color={CardColors.EMPHASIS}
      className="h-full my-0"
      childrenClassName="h-full"
    >
      <Text variant={TypographyStyles.H3} className="flex flex-row mb-4">
        <Icon
          icon={getSummaryIcon(type)}
          className="mr-2"
          rotation={type === 'same' ? 90 : undefined}
        />
        {headerText}
      </Text>
      {getSummaryTextExpenses(
        insightsType,
        type,
        amount,
        percentChange,
        currentMonth,
        prevMonth,
        percentCounterpart,
        prevMonthTotal
      )}
    </Card>
  )
}
